import type { IngestJourneysInput, JourneyCriticality } from '../api/client';

/**
 * Pure helpers for the journey authoring form (#133) — step reordering, client-side
 * validation mirroring the #21 ingest caps, and mapping the draft to the ingest
 * body. No React, no I/O: unit-testable in isolation. The server (#21/#243) stays
 * authoritative; this is a light shape check so invalid input is caught early.
 */

export const KNOWN_JOURNEY_TYPES = [
  'browse-catalog',
  'search',
  'product-detail',
  'cart-management',
  'checkout',
  'account-registration',
  'authentication',
  'subscription',
  'promo-application',
  'contact-support',
] as const;

export const KNOWN_ACTION_KINDS = ['navigate', 'click', 'input', 'submit', 'assert'] as const;

export const CRITICALITIES: JourneyCriticality[] = ['high', 'medium', 'low'];

export const MAX_STEPS = 50;
const MAX_TITLE = 200;
const MAX_DESCRIPTION = 500;
const MAX_TARGET_PATH = 2048;

/** One step as edited in the form. `key` is a stable UI identity (not persisted) so
 * React keys and focus survive reordering; `stepOrder` is derived from position. */
export interface StepDraft {
  key: string;
  actionKind: string;
  description: string;
  targetPath: string;
}

export interface JourneyFormDraft {
  journeyType: string;
  title: string;
  criticality: string;
  steps: StepDraft[];
}

/** Field-error map. Keys: `journeyType`, `title`, `criticality`, `steps`, and per
 * step `step-<index>-actionKind` / `step-<index>-description` / `step-<index>-targetPath`. */
export type FieldErrors = Record<string, string>;

let seq = 0;
/** A stable, collision-free key for a new step draft. */
export function newStepKey(): string {
  seq += 1;
  return `step-${Date.now().toString(36)}-${seq}`;
}

/** An empty step draft (defaults to the first action kind). */
export function emptyStep(): StepDraft {
  return { key: newStepKey(), actionKind: 'navigate', description: '', targetPath: '' };
}

/** Move the step at `from` to index `to`, returning a new array. Out-of-range
 * indices return the input unchanged. */
export function reorderSteps(steps: readonly StepDraft[], from: number, to: number): StepDraft[] {
  if (from === to || from < 0 || to < 0 || from >= steps.length || to >= steps.length) {
    return steps.slice();
  }
  const next = steps.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as StepDraft);
  return next;
}

/**
 * Validate a step target. Accepts a root-relative path (`/checkout`) or an absolute
 * http/https URL on the verified host (mirrors the backend `assertOnVerifiedHost`,
 * which accepts http OR https on the authorized host). Returns an error message or
 * `null` when valid/empty. The server remains authoritative.
 */
export function validateTargetPath(raw: string, authorizedHost: string): string | null {
  const value = raw.trim();
  if (!value) return null; // optional
  if (value.length > MAX_TARGET_PATH) {
    return `Target path must be ${MAX_TARGET_PATH} characters or fewer.`;
  }
  // Mirror the backend SSRF guard: control chars/backslash fold to an off-host authority.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(value)) {
    return 'Target path cannot contain control characters or backslashes.';
  }
  if (value.startsWith('//')) {
    return 'Protocol-relative URLs are not allowed.';
  }
  if (value.startsWith('/')) {
    return null; // root-relative stays on the verified host
  }
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value);
  if (!hasScheme) {
    return `Enter a root-relative path (/checkout) or an absolute URL on ${authorizedHost}.`;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'Enter a valid URL.';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return 'Only http and https targets are allowed.';
  }
  if (url.hostname.toLowerCase() !== authorizedHost.trim().toLowerCase()) {
    return `Target must stay on ${authorizedHost}.`;
  }
  return null;
}

/** Validate the whole draft, returning a field-error map (empty when valid). */
export function validateJourneyForm(draft: JourneyFormDraft, authorizedHost: string): FieldErrors {
  const errors: FieldErrors = {};

  if (!(KNOWN_JOURNEY_TYPES as readonly string[]).includes(draft.journeyType)) {
    errors.journeyType = 'Choose a journey type.';
  }

  const title = draft.title.trim();
  if (!title) {
    errors.title = 'Enter a title.';
  } else if (title.length > MAX_TITLE) {
    errors.title = `Title must be ${MAX_TITLE} characters or fewer.`;
  }

  if (!(CRITICALITIES as readonly string[]).includes(draft.criticality)) {
    errors.criticality = 'Choose a criticality.';
  }

  if (draft.steps.length > MAX_STEPS) {
    errors.steps = `A journey can have at most ${MAX_STEPS} steps.`;
  }

  draft.steps.forEach((step, index) => {
    if (!(KNOWN_ACTION_KINDS as readonly string[]).includes(step.actionKind)) {
      errors[`step-${index}-actionKind`] = 'Choose an action.';
    }
    const description = step.description.trim();
    if (!description) {
      errors[`step-${index}-description`] = 'Enter a description.';
    } else if (description.length > MAX_DESCRIPTION) {
      errors[`step-${index}-description`] = `Description must be ${MAX_DESCRIPTION} characters or fewer.`;
    }
    const targetError = validateTargetPath(step.targetPath, authorizedHost);
    if (targetError) {
      errors[`step-${index}-targetPath`] = targetError;
    }
  });

  return errors;
}

/** Map the draft to the ingest body (one journey). `stepOrder` is the array index;
 * an empty `targetPath` is omitted; empty steps are allowed (steps key omitted). */
export function toIngestInput(draft: JourneyFormDraft): IngestJourneysInput {
  const steps = draft.steps.map((step, index) => {
    const targetPath = step.targetPath.trim();
    return {
      stepOrder: index,
      actionKind: step.actionKind,
      description: step.description.trim(),
      ...(targetPath ? { targetPath } : {}),
    };
  });
  return {
    journeys: [
      {
        journeyType: draft.journeyType,
        title: draft.title.trim(),
        criticality: draft.criticality as JourneyCriticality,
        ...(steps.length ? { steps } : {}),
      },
    ],
  };
}
